import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HOT_LEAD_OPTION_ID,
  HOT_LEAD_QUESTION_ID,
  MULTI_SELECT_QUESTION_IDS,
  MACHINE_TYPE_RENTAL_QUESTIONS,
  RENTAL_RATE_QUESTION_IDS,
  CLIENT_MACHINE_PAY_QUESTIONS,
  CLIENT_PAY_RATE_QUESTION_IDS,
  getQuestionsForType,
  resolveSurveyLang,
  surveyTranslations,
} from './surveyTranslations';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import {
  addToOutbox,
  classifySubmitError,
  flushOutbox,
  pendingCount,
  submitSurveyRow,
} from '../utils/surveyOutbox';
import { LEGAL_CONTACT } from '../pages/legal/policyContent';

const NAVY = '#14213d';
const GOLD = '#C9A84C';
const PHONE_RE = /^[6-9]\d{9}$/;

function normalizeFullName(value) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

/** PAN-card style: first + last name, letters only, no digits. */
function isValidPanCardFullName(value) {
  const trimmed = normalizeFullName(value);
  if (trimmed.length < 5) return false;
  if (/\d/.test(trimmed)) return false;
  if (!/^[\p{L}\s.'-]+$/u.test(trimmed)) return false;
  const words = trimmed.split(' ').filter(Boolean);
  return words.length >= 2 && words.every((w) => w.length >= 2);
}

function isValidSurveyPhone(value) {
  const phone = String(value || '').replace(/\D/g, '');
  return phone.length === 10 && PHONE_RE.test(phone);
}

function selectedMachineTypes(answers) {
  return Array.isArray(answers?.machine_types) ? answers.machine_types : [];
}

function isConditionalRateQuestionVisible(questionId, answers, surveyType) {
  if (surveyType === 'owner' && RENTAL_RATE_QUESTION_IDS.includes(questionId)) {
    const machineId = Object.entries(MACHINE_TYPE_RENTAL_QUESTIONS)
      .find(([, rentalQid]) => rentalQid === questionId)?.[0];
    if (!machineId) return true;
    return selectedMachineTypes(answers).includes(machineId);
  }
  if (surveyType === 'client' && CLIENT_PAY_RATE_QUESTION_IDS.includes(questionId)) {
    const selected = answers?.machine_types_needed;
    if (!selected || typeof selected !== 'string') return false;
    return CLIENT_MACHINE_PAY_QUESTIONS[selected] === questionId;
  }
  if (RENTAL_RATE_QUESTION_IDS.includes(questionId)
    || CLIENT_PAY_RATE_QUESTION_IDS.includes(questionId)) {
    return false;
  }
  return true;
}

function visibleStepQuestionIds(stepDef, answers = {}, surveyType = '') {
  if (!stepDef) return [];
  return stepDef.questionIds.filter((qid) => (
    isConditionalRateQuestionVisible(qid, answers, surveyType)
  ));
}

/** ST4 — profession-wise client pain question IDs (client_pain1). */
function getProfessionPainIds(profession) {
  switch (profession) {
    case 'farmer':
      return ['farmerPain1', 'farmerPain2', 'farmerPain3'];
    case 'road':
      return ['roadPain1', 'roadPain2', 'roadPain3'];
    case 'industrial':
      return ['industrialPain1', 'industrialPain2', 'industrialPain3'];
    case 'government':
      return ['govtPain1', 'govtPain2', 'govtPain3'];
    case 'builder':
      return ['builderPain1', 'builderPain2', 'builderPain3'];
    case 'well':
      return ['wellPain1', 'wellPain2', 'wellPain3'];
    default:
      return ['inf_rounding_loss', 'inf_punctuality', 'inf_fuel_surcharge'];
  }
}

/** ST5 — owner type-wise pain question IDs (owner_pain1). */
function getOwnerTypePainIds(ownerType) {
  switch (ownerType) {
    case 'single':
      return ['singlePain1', 'singlePain2', 'singlePain3'];
    case 'fleet':
      return ['fleetPain1', 'fleetPain2', 'fleetPain3'];
    case 'contractor_owner':
      return ['contractorOwnerPain1', 'contractorOwnerPain2', 'contractorOwnerPain3'];
    default:
      return ['inf_diesel_theft', 'inf_emi_blocked', 'inf_side_booking'];
  }
}

const ALL_OWNER_TYPE_PAIN_IDS = [
  'singlePain1', 'singlePain2', 'singlePain3',
  'fleetPain1', 'fleetPain2', 'fleetPain3',
  'contractorOwnerPain1', 'contractorOwnerPain2', 'contractorOwnerPain3',
  'inf_diesel_theft', 'inf_emi_blocked', 'inf_side_booking',
];

function buildJoinWhatsAppUrl(pitch, { name, phone, village }) {
  const text = (pitch.waMessage || '')
    .replace('{name}', name || '—')
    .replace('{phone}', phone || '—')
    .replace('{village}', village || '—');
  const base = LEGAL_CONTACT.whatsapp || 'https://wa.me/918408000084';
  return `${base}?text=${encodeURIComponent(text)}`;
}

function ClosingPitchBlock({ pitch, showCta, ctaHref, compact }) {
  if (!pitch) return null;
  return (
    <div
      style={{
        marginBottom: compact ? '16px' : '24px',
        padding: compact ? '16px 14px' : '20px 18px',
        borderRadius: '14px',
        background: `linear-gradient(145deg, rgba(201,168,76,0.14) 0%, rgba(20,33,61,0.6) 100%)`,
        border: `1px solid rgba(201,168,76,0.45)`,
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      }}
    >
      <p
        style={{
          margin: '0 0 10px',
          fontSize: '11px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: GOLD,
          fontWeight: 700,
        }}
      >
        Development Express
      </p>
      <h3
        style={{
          margin: '0 0 12px',
          fontSize: compact ? '16px' : '18px',
          fontWeight: 800,
          lineHeight: 1.35,
          color: '#fff',
        }}
      >
        {pitch.headline}
      </h3>
      <p
        style={{
          margin: '0 0 12px',
          fontSize: '14px',
          lineHeight: 1.65,
          color: '#d4dce8',
        }}
      >
        {pitch.body}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: '15px',
          fontWeight: 700,
          fontStyle: 'italic',
          color: GOLD,
          lineHeight: 1.45,
        }}
      >
        {pitch.tagline}
      </p>
      {showCta && ctaHref && (
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            marginTop: '18px',
            padding: '16px 20px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, #25D366 0%, #128C7E 100%)`,
            color: '#fff',
            fontWeight: 800,
            fontSize: '16px',
            textAlign: 'center',
            textDecoration: 'none',
            letterSpacing: '0.02em',
            boxShadow: '0 4px 16px rgba(37,211,102,0.35)',
          }}
        >
          {pitch.cta}
          <span
            style={{
              display: 'block',
              marginTop: '4px',
              fontSize: '12px',
              fontWeight: 500,
              opacity: 0.9,
            }}
          >
            {pitch.ctaHint}
          </span>
        </a>
      )}
    </div>
  );
}

const SURVEY_STEP_PLAN = {
  owner: [
    { key: 'contact', questionIds: [], includeConsent: false },
    {
      key: 'profile',
      questionIds: [
        'ownerType',
        'machines_count',
        'machine_types',
        'jcb_3dx_rental_rate',
        'excavator_rental_rate',
        'mini_excavator_rental_rate',
      ],
      includeConsent: false,
    },
    {
      key: 'owner_pain1',
      questionIds: ['inf_diesel_theft', 'inf_emi_blocked', 'inf_side_booking'],
      includeConsent: false,
    },
    {
      key: 'finish',
      questionIds: ['uses_whatsapp'],
      includeConsent: true,
    },
  ],
  client: [
    { key: 'contact', questionIds: [], includeConsent: false },
    {
      key: 'profile',
      questionIds: [
        'clientProfession',
        'hire_frequency',
        'machine_types_needed',
        'jcb_3dx_pay_rate',
        'excavator_pay_rate',
        'mini_excavator_pay_rate',
      ],
      includeConsent: false,
    },
    {
      key: 'client_pain1',
      questionIds: ['inf_rounding_loss', 'inf_punctuality', 'inf_fuel_surcharge'],
      includeConsent: false,
    },
    {
      key: 'client_pain2',
      questionIds: ['inf_advance_trust', 'inf_digital_ledger'],
      includeConsent: false,
    },
    {
      key: 'finish',
      questionIds: ['uses_whatsapp'],
      includeConsent: true,
    },
  ],
  pm: [
    { key: 'contact', questionIds: [], includeConsent: false },
    {
      key: 'profile',
      questionIds: ['pmProjectsCount', 'pmSourceMethod'],
      includeConsent: false,
    },
    {
      key: 'pain',
      questionIds: ['pmBiggestChallenge', 'pmBudgetRange', 'pmDecisionAuthority'],
      includeConsent: false,
    },
    {
      key: 'finish',
      questionIds: ['uses_whatsapp'],
      includeConsent: true,
    },
  ],
  contractor: [
    { key: 'contact', questionIds: [], includeConsent: false },
    {
      key: 'profile',
      questionIds: ['contractorProjectType', 'contractorHireFrequency'],
      includeConsent: false,
    },
    {
      key: 'pain',
      questionIds: ['contractorPain', 'contractorGst', 'contractorPayment'],
      includeConsent: false,
    },
    {
      key: 'finish',
      questionIds: ['uses_whatsapp'],
      includeConsent: true,
    },
  ],
};

const STEP_COPY = {
  mr: {
    back: '← मागे',
    next: 'पुढे →',
    stepOf: (n, total) => `पायरी ${n} / ${total}`,
    offlineBanner: 'Network नाही — form भरू शकता; submit केल्यावर offline save होईल.',
    owner: ['संपर्क', 'मशीन माहिती', 'मालक प्रकार प्रश्न', 'शेवट'],
    client: ['संपर्क', 'तुमची माहिती', 'ग्राहक — १', 'ग्राहक — २', 'शेवट'],
    pm: ['संपर्क', 'प्रोफाइल', 'आव्हाने', 'शेवट'],
    contractor: ['संपर्क', 'प्रोफाइल', 'आव्हाने', 'शेवट'],
  },
  hi: {
    back: '← वापस',
    next: 'आगे →',
    stepOf: (n, total) => `चरण ${n} / ${total}`,
    offlineBanner: 'Network नहीं — फॉर्म भर सकते हैं; submit पर offline save होगा।',
    owner: ['संपर्क', 'मशीन', 'मालिक प्रकार प्रश्न', 'अंत'],
    client: ['संपर्क', 'प्रोफाइल', 'ग्राहक 1', 'ग्राहक 2', 'अंत'],
    pm: ['संपर्क', 'प्रोफाइल', 'चुनौतियाँ', 'अंत'],
    contractor: ['संपर्क', 'प्रोफाइल', 'चुनौतियाँ', 'अंत'],
  },
  en: {
    back: '← Back',
    next: 'Next →',
    stepOf: (n, total) => `Step ${n} of ${total}`,
    offlineBanner: 'No network — you can still fill the form; it will sync when online.',
    owner: ['Contact', 'Machine info', 'Owner type pains', 'Finish'],
    client: ['Contact', 'Profile', 'Client 1', 'Client 2', 'Finish'],
    pm: ['Contact', 'Profile', 'Challenges', 'Finish'],
    contractor: ['Contact', 'Profile', 'Challenges', 'Finish'],
  },
};

const shellStyle = {
  minHeight: '100vh',
  background: `linear-gradient(180deg, ${NAVY} 0%, #0a1628 100%)`,
  color: '#e8ecf1',
  fontFamily: "'Noto Sans Devanagari', 'Plus Jakarta Sans', sans-serif",
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

const cardStyle = {
  maxWidth: '560px',
  margin: '0 auto',
  padding: '16px 16px 48px',
};

function normalizeSurveyType(type) {
  const t = String(type || '').toLowerCase();
  if (t === 'owner' || t === 'client') return t;
  return null;
}

const VALID_OWNER_TYPES = new Set(['single', 'fleet', 'contractor_owner']);
const VALID_CLIENT_PROFESSIONS = new Set([
  'farmer', 'road', 'industrial', 'government', 'builder', 'well', 'other',
]);

function SurveyForm() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const surveyType = normalizeSurveyType(type);

  const source = searchParams.get('src') || null;
  const surveyor = searchParams.get('by') || null;
  const ownerTypeFromUrl = VALID_OWNER_TYPES.has(searchParams.get('ownerType'))
    ? searchParams.get('ownerType')
    : null;
  const professionFromUrl = VALID_CLIENT_PROFESSIONS.has(searchParams.get('profession'))
    ? searchParams.get('profession')
    : null;

  const { lang: appLang } = useLanguage();
  const lang = useMemo(() => resolveSurveyLang(appLang), [appLang]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const init = {};
    if (ownerTypeFromUrl) init.ownerType = ownerTypeFromUrl;
    if (professionFromUrl) init.clientProfession = professionFromUrl;
    return init;
  });
  const [respondentName, setRespondentName] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [village, setVillage] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hotLead, setHotLead] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [outboxPending, setOutboxPending] = useState(pendingCount);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const t = surveyTranslations[lang] || surveyTranslations.mr;
  const stepCopy = STEP_COPY[lang] || STEP_COPY.mr;
  const steps = useMemo(() => {
    const base = (surveyType && SURVEY_STEP_PLAN[surveyType]) || [];
    if (surveyType === 'client') {
      return base.map((s) => {
        if (s.key === 'profile' && professionFromUrl) {
          return {
            ...s,
            questionIds: s.questionIds.filter((qid) => qid !== 'clientProfession'),
          };
        }
        if (s.key === 'client_pain1') {
          return { ...s, questionIds: getProfessionPainIds(answers.clientProfession) };
        }
        return s;
      });
    }
    if (surveyType === 'owner') {
      return base.map((s) => {
        if (s.key === 'profile' && ownerTypeFromUrl) {
          return {
            ...s,
            questionIds: s.questionIds.filter((qid) => qid !== 'ownerType'),
          };
        }
        if (s.key === 'owner_pain1') {
          return { ...s, questionIds: getOwnerTypePainIds(answers.ownerType) };
        }
        return s;
      });
    }
    return base;
  }, [surveyType, answers.clientProfession, answers.ownerType, ownerTypeFromUrl, professionFromUrl]);
  const stepCount = steps.length;
  const currentStep = steps[step];
  const isLastStep = stepCount > 0 && step >= stepCount - 1;

  const questions = useMemo(
    () => (surveyType && SURVEY_STEP_PLAN[surveyType] ? getQuestionsForType(lang, surveyType) : {}),
    [lang, surveyType],
  );

  const stepTitle = surveyType && stepCopy[surveyType]
    ? stepCopy[surveyType][step] || ''
    : '';

  const headerTitle = (surveyType && t.titles?.[surveyType]) || t.titles.client;

  const progressPct = stepCount > 0 ? ((step + 1) / stepCount) * 100 : 0;

  const isContactStepValid = useMemo(() => (
    isValidPanCardFullName(respondentName)
    && isValidSurveyPhone(whatsappNumber)
    && village.trim().length > 0
  ), [respondentName, whatsappNumber, village]);

  const canGoNext = currentStep?.key !== 'contact' || isContactStepValid;

  const clearFieldError = useCallback((key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const refreshPending = useCallback(() => {
    setOutboxPending(pendingCount());
  }, []);

  const runFlush = useCallback(async () => {
    const result = await flushOutbox();
    if (result.flushed > 0) {
      refreshPending();
    }
    return result;
  }, [refreshPending]);

  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    document.addEventListener('contextmenu', prevent);
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    const onBeforePrint = () => {
      document.body.style.visibility = 'hidden';
    };
    const onAfterPrint = () => {
      document.body.style.visibility = '';
    };
    window.addEventListener('beforeprint', onBeforePrint);
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
      window.removeEventListener('beforeprint', onBeforePrint);
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.style.visibility = '';
    };
  }, []);

  useEffect(() => {
    refreshPending();
    runFlush();
    const onOnline = () => {
      setIsOnline(true);
      runFlush().then(refreshPending);
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [runFlush, refreshPending]);

  const setAnswer = (questionId, optionId) => {
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: optionId };
      if (questionId === 'machine_types_needed') {
        CLIENT_PAY_RATE_QUESTION_IDS.forEach((payQid) => {
          if (CLIENT_MACHINE_PAY_QUESTIONS[optionId] !== payQid) delete next[payQid];
        });
      }
      if (questionId === 'ownerType') {
        const keep = new Set(getOwnerTypePainIds(optionId));
        ALL_OWNER_TYPE_PAIN_IDS.forEach((qid) => {
          if (!keep.has(qid)) delete next[qid];
        });
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const toggleMultiAnswer = (questionId, optionId) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const nextIds = current.includes(optionId)
        ? current.filter((id) => id !== optionId)
        : [...current, optionId];
      const next = { ...prev, [questionId]: nextIds };
      if (questionId === 'machine_types') {
        Object.entries(MACHINE_TYPE_RENTAL_QUESTIONS).forEach(([machineId, rentalQid]) => {
          if (!nextIds.includes(machineId)) delete next[rentalQid];
        });
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const isAnswered = (questionId) => {
    const val = answers[questionId];
    if (MULTI_SELECT_QUESTION_IDS.includes(questionId)) {
      return Array.isArray(val) && val.length > 0;
    }
    return Boolean(val);
  };

  const validateStep = (stepIndex) => {
    const next = {};
    const stepDef = steps[stepIndex];
    if (!stepDef) return false;

    if (stepDef.key === 'contact') {
      const normalizedName = normalizeFullName(respondentName);
      if (!normalizedName) next.name = t.errors.nameRequired;
      else if (!isValidPanCardFullName(normalizedName)) next.name = t.errors.nameInvalid;
      const phone = whatsappNumber.replace(/\D/g, '');
      if (!phone) next.phone = t.errors.phoneRequired;
      else if (!isValidSurveyPhone(phone)) next.phone = t.errors.phoneInvalid;
      if (!village.trim()) next.village = t.errors.villageRequired;
    }

    visibleStepQuestionIds(stepDef, answers, surveyType).forEach((qid) => {
      if (!isAnswered(qid)) {
        if (qid === 'ownerType') next[qid] = t.errors.ownerTypeRequired || t.errors.answerRequired;
        else if (qid === 'clientProfession') next[qid] = t.errors.clientProfessionRequired || t.errors.answerRequired;
        else next[qid] = t.errors.answerRequired;
      }
    });

    if (stepDef.includeConsent && !consent) {
      next.consent = t.errors.consentRequired;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => {
    const phone = whatsappNumber.replace(/\D/g, '');
    const trialAnswer = answers[HOT_LEAD_QUESTION_ID];
    const isHotLead = trialAnswer === HOT_LEAD_OPTION_ID;
    const finalAnswers = { ...answers };

    return {
      survey_type: surveyType,
      answers: finalAnswers,
      respondent_name: normalizeFullName(respondentName),
      whatsapp_number: phone,
      village: village.trim(),
      hot_lead: isHotLead,
      consent: true,
      consent_text: t.consent,
      source,
      surveyor,
      language: lang,
      submitted_at: new Date().toISOString(),
      ip_hash: null,
    };
  };

  const handleNext = () => {
    setStatusMsg('');
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, stepCount - 1));
  };

  const handleBack = () => {
    setStatusMsg('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg('');
    if (!surveyType) return;
    if (!validateStep(step)) return;

    setSubmitting(true);
    const row = buildPayload();
    const isHot = row.hot_lead;

    try {
      const result = await submitSurveyRow(row);
      if (result.duplicate) {
        setStatusMsg(t.errors.duplicateSurveyed || t.errors.duplicate);
        setSubmitting(false);
        return;
      }
      if (result.blockedReferral) {
        setStatusMsg(t.errors.blockedReferral);
        setSubmitting(false);
        return;
      }
      if (!result.ok) {
        setStatusMsg(result.rateLimit ? t.errors.rateLimit : t.errors.generic);
        setSubmitting(false);
        return;
      }
      setHotLead(isHot);
      setSubmitted(true);
    } catch (error) {
      const offline = !navigator.onLine || error?.message === 'offline';
      if (offline) {
        addToOutbox(row);
        refreshPending();
        setHotLead(isHot);
        setSubmitted(true);
        setStatusMsg(t.offlineSaved);
      } else {
        setStatusMsg(classifySubmitError(error, t));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!surveyType) {
    return (
      <div style={shellStyle}>
        <div style={{ ...cardStyle, paddingTop: '40vh', textAlign: 'center' }}>
          <p style={{ color: GOLD }}>{surveyTranslations.mr.errors.invalidSurvey}</p>
        </div>
      </div>
    );
  }

  // ST1: pm/contractor accepted; question plans land in ST2.
  if (stepCount === 0) {
    return (
      <div style={shellStyle}>
        <div style={{ ...cardStyle, paddingTop: '28vh', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.12em', color: GOLD, textTransform: 'uppercase' }}>
            {t.brand}
          </p>
          <h1 style={{ margin: '8px 0 12px', fontSize: 20, color: '#fff' }}>{headerTitle}</h1>
          <p style={{ margin: 0, color: '#9aa8b8', lineHeight: 1.55 }}>
            Questions for this role are being prepared.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const pitch = t.closingPitch?.[surveyType];
    const waJoinUrl = pitch
      ? buildJoinWhatsAppUrl(pitch, {
        name: respondentName.trim(),
        phone: whatsappNumber.replace(/\D/g, ''),
        village: village.trim(),
      })
      : null;

    return (
      <div style={shellStyle}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '14px 16px 0' }}>
          <div style={{ ...cardStyle, padding: 0, width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <LanguageSelector compact />
          </div>
        </div>
        <div style={{ ...cardStyle, paddingTop: '4vh', textAlign: 'center' }}>
          <div
            style={{
              background: 'rgba(201,168,76,0.08)',
              border: `1px solid ${GOLD}`,
              borderRadius: '16px',
              padding: '28px 20px',
              textAlign: 'left',
            }}
          >
            <p style={{ fontSize: '48px', margin: '0 0 12px', textAlign: 'center' }}>
              {String.fromCodePoint(0x2705)}
            </p>
            <h1 style={{ color: GOLD, margin: '0 0 12px', fontSize: '24px', textAlign: 'center' }}>
              {t.thankYou.title}
            </h1>
            <p style={{ margin: '0 0 16px', lineHeight: 1.6, color: '#c8d0dc', textAlign: 'center' }}>
              {t.thankYou.body}
            </p>
            {hotLead && (
              <p style={{ margin: '0 0 16px', color: GOLD, fontWeight: 600, lineHeight: 1.5, textAlign: 'center' }}>
                {t.thankYou.hotLead}
              </p>
            )}
            <ClosingPitchBlock pitch={pitch} showCta ctaHref={waJoinUrl} compact />
            {statusMsg && (
              <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#8896a8', textAlign: 'center' }}>
                {statusMsg}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const pendingLabel = t.pendingSync.replace('{count}', String(outboxPending));
  const closingPitch = t.closingPitch?.[surveyType];

  return (
    <div style={shellStyle}>
      <header
        style={{
          background: NAVY,
          borderBottom: `1px solid rgba(201,168,76,0.25)`,
          padding: '14px 16px 0',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ ...cardStyle, padding: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '11px', letterSpacing: '0.12em', color: GOLD, textTransform: 'uppercase' }}>
              {t.brand}
            </p>
            <h1 style={{ margin: '4px 0 0', fontSize: '18px', fontWeight: 700, color: '#fff' }}>
              {headerTitle}
            </h1>
            {outboxPending > 0 && (
              <span
                style={{
                  display: 'inline-block',
                  marginTop: '8px',
                  fontSize: '11px',
                  background: 'rgba(201,168,76,0.15)',
                  border: `1px solid ${GOLD}`,
                  color: GOLD,
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}
              >
                {pendingLabel}
              </span>
            )}
          </div>
          <LanguageSelector compact />
        </div>
        <div
          style={{
            maxWidth: '560px',
            margin: '12px auto 0',
            height: '4px',
            background: 'rgba(255,255,255,0.12)',
            borderRadius: '2px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progressPct}%`,
              background: GOLD,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </header>

      {!isOnline && (
        <div
          style={{
            background: 'rgba(201,168,76,0.12)',
            borderBottom: `1px solid rgba(201,168,76,0.35)`,
            padding: '10px 16px',
            fontSize: '13px',
            color: GOLD,
            textAlign: 'center',
          }}
        >
          {stepCopy.offlineBanner}
        </div>
      )}

      <main style={cardStyle}>
        <form onSubmit={handleSubmit} noValidate>
          <p style={stepLabelStyle}>{stepCopy.stepOf(step + 1, stepCount)}</p>
          <h2 style={stepTitleStyle}>{stepTitle}</h2>

          {currentStep?.key === 'contact' && (
            <section style={{ marginBottom: '8px' }}>
              <Field
                label={t.fields.name}
                value={respondentName}
                onChange={(v) => {
                  setRespondentName(v.replace(/\d/g, ''));
                  clearFieldError('name');
                }}
                placeholder={t.fields.namePlaceholder}
                error={errors.name}
                hint={t.fields.nameHint}
                inputMode="text"
              />
              <Field
                label={t.fields.phone}
                value={whatsappNumber}
                onChange={(v) => {
                  setWhatsappNumber(v.replace(/\D/g, '').slice(0, 10));
                  clearFieldError('phone');
                }}
                placeholder={t.fields.phonePlaceholder}
                error={errors.phone}
                hint={t.fields.phoneHint}
                inputMode="numeric"
                maxLength={10}
              />
              <Field
                label={t.fields.village}
                value={village}
                onChange={(v) => {
                  setVillage(v);
                  clearFieldError('village');
                }}
                placeholder={t.fields.villagePlaceholder}
                error={errors.village}
                inputMode="text"
              />
            </section>
          )}

          {visibleStepQuestionIds(currentStep, answers, surveyType).map((qid) => {
            const q = questions[qid];
            if (!q) return null;
            const isHotQuestion = qid === HOT_LEAD_QUESTION_ID;
            return (
              <div key={qid} style={{ marginBottom: '20px' }}>
                <p style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: 600, lineHeight: 1.45 }}>
                  {q.label}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {q.options.map((opt) => {
                    const isMulti = MULTI_SELECT_QUESTION_IDS.includes(qid);
                    const selected = isMulti
                      ? (Array.isArray(answers[qid]) && answers[qid].includes(opt.id))
                      : answers[qid] === opt.id;
                    const isHotOption = isHotQuestion && opt.id === HOT_LEAD_OPTION_ID;
                    return (
                      <label
                        key={opt.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px 14px',
                          borderRadius: '10px',
                          border: selected
                            ? `1px solid ${isHotOption ? '#4ade80' : GOLD}`
                            : '1px solid rgba(255,255,255,0.12)',
                          background: selected
                            ? (isHotOption ? 'rgba(74,222,128,0.12)' : 'rgba(201,168,76,0.12)')
                            : 'rgba(0,0,0,0.2)',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type={isMulti ? 'checkbox' : 'radio'}
                          name={qid}
                          value={opt.id}
                          checked={selected}
                          onChange={() => (
                            isMulti ? toggleMultiAnswer(qid, opt.id) : setAnswer(qid, opt.id)
                          )}
                          style={{ accentColor: isHotOption ? '#4ade80' : GOLD }}
                        />
                        <span style={{ fontSize: '14px', lineHeight: 1.4, flex: 1 }}>{opt.text}</span>
                        {isHotOption && selected && (
                          <span style={{ fontSize: '14px' }}>{String.fromCodePoint(0x1F525)}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
                {errors[qid] && <p style={errorTextStyle}>{errors[qid]}</p>}
              </div>
            );
          })}

          {currentStep?.key === 'finish' && closingPitch && (
            <ClosingPitchBlock pitch={closingPitch} showCta={false} />
          )}

          {currentStep?.includeConsent && (
            <>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  marginBottom: '20px',
                  padding: '14px',
                  borderRadius: '10px',
                  border: errors.consent ? '1px solid #e57373' : '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(0,0,0,0.2)',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (e.target.checked) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.consent;
                        return next;
                      });
                    }
                  }}
                  style={{ marginTop: '3px', accentColor: GOLD }}
                />
                <span style={{ fontSize: '13px', lineHeight: 1.5, color: '#c8d0dc' }}>{t.consent}</span>
              </label>
              {errors.consent && <p style={{ ...errorTextStyle, marginTop: '-12px' }}>{errors.consent}</p>}
            </>
          )}

          {statusMsg && !submitted && (
            <p
              style={{
                margin: '0 0 16px',
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(229,115,115,0.12)',
                border: '1px solid #e57373',
                color: '#ffcdd2',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
            >
              {statusMsg}
            </p>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            {step > 0 && (
              <button
                type="button"
                onClick={handleBack}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  background: 'rgba(0,0,0,0.2)',
                  color: '#e8ecf1',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {stepCopy.back}
              </button>
            )}
            {isLastStep ? (
              <button
                type="submit"
                disabled={submitting}
                style={{
                  flex: step > 0 ? 2 : 1,
                  padding: '14px',
                  border: 'none',
                  borderRadius: '10px',
                  background: submitting ? 'rgba(201,168,76,0.5)' : `linear-gradient(135deg, #a07830, ${GOLD})`,
                  color: NAVY,
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: submitting ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {submitting ? t.buttons.submitting : t.buttons.submit}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext}
                style={{
                  flex: step > 0 ? 2 : 1,
                  padding: '14px',
                  border: 'none',
                  borderRadius: '10px',
                  background: canGoNext
                    ? `linear-gradient(135deg, #a07830, ${GOLD})`
                    : 'rgba(201,168,76,0.25)',
                  color: NAVY,
                  fontWeight: 700,
                  fontSize: '15px',
                  cursor: canGoNext ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  opacity: canGoNext ? 1 : 0.7,
                }}
              >
                {stepCopy.next}
              </button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}

const stepLabelStyle = {
  margin: '0 0 6px',
  fontSize: '11px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#8896a8',
  fontWeight: 700,
};

const stepTitleStyle = {
  margin: '0 0 20px',
  fontSize: '18px',
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.35,
};

const errorTextStyle = {
  margin: '6px 0 0',
  fontSize: '12px',
  color: '#ef9a9a',
};

function Field({
  label, value, onChange, placeholder, error, hint, inputMode, maxLength,
}) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>
        {label}
      </label>
      {hint && (
        <p style={{ margin: '0 0 6px', fontSize: '11px', lineHeight: 1.4, color: '#8896a8' }}>
          {hint}
        </p>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '12px 14px',
          borderRadius: '10px',
          border: error ? '1px solid #e57373' : '1px solid rgba(255,255,255,0.15)',
          background: 'rgba(0,0,0,0.25)',
          color: '#fff',
          fontSize: '15px',
          fontFamily: 'inherit',
          userSelect: 'text',
          WebkitUserSelect: 'text',
        }}
      />
      {error && <p style={errorTextStyle}>{error}</p>}
    </div>
  );
}

export default SurveyForm;
